// CSR-1128: Non-Trucking Liability (NTL) and standard Auto Liability (AL) are
// mutually exclusive on a policy. NTL is rendered INSIDE the ACORD 25 Auto
// Liability section (on the additional/OTHER COVERAGE line) rather than as a
// separate OTHER coverage row, and the AMS policy coverages tab suppresses the
// (empty) AL card when NTL is active. Both the client display and the server
// COI builder import this single predicate so the rule cannot drift between the
// two runtimes.

// Mirror of the `hasValue` rule used in `policy-coverages.ts` and
// `coiTemplate.ts`: a value is real when it is non-empty and not a zero
// sentinel ("0", "0.0", "000", ...).
export function isRealValue(v: unknown): boolean {
  if (v == null) return false;
  const s = String(v).trim();
  if (!s) return false;
  return !/^0+(\.0+)?$/.test(s);
}

// Accepts the AMS-internal `PolicyCoverages` shape and the loose `Record`-cast
// shape used in `coiTemplate.ts`. The `nonTruckingLiability` block is either a
// `{ limit, status? }` object, a bare string limit, or absent.
export function isNonTruckingLiabilityActive(
  cov: { nonTruckingLiability?: unknown } | null | undefined,
): boolean {
  const ntl = cov?.nonTruckingLiability;
  if (ntl == null) return false;
  if (typeof ntl === "string") return isRealValue(ntl);
  if (typeof ntl !== "object") return false;
  const block = ntl as { limit?: unknown; status?: unknown };
  if (block.status === "removed") return false;
  return isRealValue(block.limit);
}

// CSR-2118: the standard (non-NTL) Auto Liability limits. An AL block carrying
// one of these is a real liability line; one carrying only UM / PIP / medical
// values beside an NTL limit is Progressive's NTL slice, not Auto Liability.
// Both the client card predicate and the server DC translator key off this.
export const STANDARD_AUTO_LIABILITY_LIMIT_KEYS = [
  "combinedSingleLimit",
  "bodilyInjuryPerPerson",
  "bodilyInjuryPerAccident",
  "propertyDamage",
] as const;

export function hasStandardAutoLiabilityLimit(block: unknown): boolean {
  if (block == null || typeof block !== "object") return false;
  const al = block as Record<string, unknown>;
  return STANDARD_AUTO_LIABILITY_LIMIT_KEYS.some((key) => isRealValue(al[key]));
}
